import { useEffect, useMemo, useState, type FormEvent } from "react";
import {
  answerWebOrderQuestion,
  cancelWebOrderDropoff,
  fetchWebOrder,
  fetchWebOrderDropoffAvailability,
  fetchWebOrderPickupAvailability,
  formatFirebaseError,
  scheduleWebOrderDropoff,
  submitWebOrderNote,
  submitWebOrderPhoto,
  submitWebOrderPickup,
  warmup,
} from "../api";
import { SlotPicker } from "../components/SlotPicker";
import { resizeImageFileForUpload } from "../imageUtils";
import { detectLocale, t, type Locale } from "../i18n";
import { LegalFooter } from "../LegalFooter";
import { formatPrice, formatQty, formatSlotDate, formatSlotTime, nextDays, unitLabelShort } from "../slots";
import type { WebOrderData, WebOrderPickupSlot } from "../types";
import { applySalonBranding } from "../branding";

type View = "email" | "details" | "pickup" | "pickupSuccess" | "dropoff";

const PHASES = ["received", "quote", "in_progress", "ready", "done"] as const;
const PHASE_LABELS: Record<(typeof PHASES)[number], "phaseReceived" | "phaseQuote" | "phaseInProgress" | "phaseReady" | "phaseDone"> = {
  received: "phaseReceived",
  quote: "phaseQuote",
  in_progress: "phaseInProgress",
  ready: "phaseReady",
  done: "phaseDone",
};

function emailStorageKey(claimCode: string) {
  return `werkstate_order_email_${claimCode}`;
}

function berlinLocalDate(dateKey: string): Date {
  const [y, m, d] = dateKey.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d, 12, 0, 0));
}

export function OrderPage({ claimCode }: { claimCode: string }) {
  const [locale] = useState<Locale>(detectLocale());
  const [view, setView] = useState<View>("email");
  const [email, setEmail] = useState("");
  const [order, setOrder] = useState<WebOrderData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pickupDateKey, setPickupDateKey] = useState<string | null>(null);
  const [pickupSlots, setPickupSlots] = useState<WebOrderPickupSlot[]>([]);
  const [pickupLoading, setPickupLoading] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState<Date | null>(null);
  const [submittingPickup, setSubmittingPickup] = useState(false);
  const [confirmedPickup, setConfirmedPickup] = useState<{ startAt: string; endAt: string } | null>(
    null
  );
  const [customerNoteText, setCustomerNoteText] = useState("");
  const [submittingNote, setSubmittingNote] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [contributionMessage, setContributionMessage] = useState<string | null>(null);
  const [dropoffEmployeeId, setDropoffEmployeeId] = useState<string | null>(null);
  const [dropoffStartAt, setDropoffStartAt] = useState<string | null>(null);
  const [submittingDropoff, setSubmittingDropoff] = useState(false);
  const [cancelingDropoff, setCancelingDropoff] = useState(false);
  const [inquiryText, setInquiryText] = useState("");
  const [submittingInquiry, setSubmittingInquiry] = useState(false);

  const pickupDays = useMemo(() => nextDays(14), []);

  useEffect(() => {
    applySalonBranding(order?.salon.branding ?? null);
  }, [order?.salon.branding]);

  useEffect(() => {
    const stored = sessionStorage.getItem(emailStorageKey(claimCode));
    if (stored) {
      setEmail(stored);
      void loadOrder(stored);
    }
  }, [claimCode]);

  async function loadOrder(emailValue: string) {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchWebOrder({ claimCode, email: emailValue, locale });
      setOrder(result);
      sessionStorage.setItem(emailStorageKey(claimCode), emailValue);
      setView("details");
    } catch (e) {
      const message = formatFirebaseError(e);
      if (message.toLowerCase().includes("invalid code or email")) {
        setError(t(locale, "orderAccessDenied"));
      } else {
        setError(message);
      }
      setView("email");
    } finally {
      setLoading(false);
    }
  }

  const orderLoaded = order != null;
  useEffect(() => {
    if (!orderLoaded) return;
    if (view === "details") {
      warmup("addWebOrderNote", "addWebOrderAttachment", "answerWebOrderQuestion");
      warmup("getWebOrderPickupAvailability", "getWebOrderDropoffAvailability");
    } else if (view === "pickup") {
      warmup("scheduleWebOrderPickup");
    } else if (view === "dropoff") {
      warmup("scheduleWebOrderDropoff");
    }
  }, [orderLoaded, view]);

  function refreshOrderInBackground() {
    void fetchWebOrder({ claimCode, email, locale })
      .then(setOrder)
      .catch(() => undefined);
  }

  useEffect(() => {
    if (view !== "pickup" || !order || !pickupDateKey || !email) return;
    let cancelled = false;
    (async () => {
      try {
        setPickupLoading(true);
        const result = await fetchWebOrderPickupAvailability({
          claimCode,
          email,
          date: pickupDateKey,
          employeeId: order.pickupEmployeeId,
        });
        if (!cancelled) setPickupSlots(result.slots);
      } catch (e) {
        if (!cancelled) {
          setPickupSlots([]);
          setError(formatFirebaseError(e));
        }
      } finally {
        if (!cancelled) setPickupLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [view, order, pickupDateKey, email, claimCode]);

  async function handleEmailSubmit(event: FormEvent) {
    event.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) return;
    await loadOrder(trimmed);
  }

  async function handleAddNote() {
    const trimmed = customerNoteText.trim();
    if (!trimmed) return;
    setSubmittingNote(true);
    setContributionMessage(null);
    setError(null);
    try {
      await submitWebOrderNote({ claimCode, email, text: trimmed });
      setCustomerNoteText("");
      setContributionMessage(t(locale, "orderCustomerNoteAdded"));
      refreshOrderInBackground();
    } catch (e) {
      setError(formatFirebaseError(e));
    } finally {
      setSubmittingNote(false);
    }
  }

  async function handlePhotoSelected(file: File | null) {
    if (!file || !email) return;
    setUploadingPhoto(true);
    setContributionMessage(null);
    setError(null);
    try {
      const payload = await resizeImageFileForUpload(file);
      await submitWebOrderPhoto({ claimCode, email, ...payload });
      setContributionMessage(t(locale, "orderCustomerPhotoAdded"));
      refreshOrderInBackground();
    } catch (e) {
      setError(formatFirebaseError(e));
    } finally {
      setUploadingPhoto(false);
    }
  }

  async function handlePickupConfirm() {
    if (!order || !selectedSlot) return;
    setSubmittingPickup(true);
    setError(null);
    try {
      const result = await submitWebOrderPickup({
        claimCode,
        email,
        employeeId: order.pickupEmployeeId,
        startAt: selectedSlot.toISOString(),
      });
      setConfirmedPickup(result.pickupAppointment);
      setOrder((current) =>
        current ? { ...current, pickupAppointment: result.pickupAppointment } : current
      );
      setView("pickupSuccess");
      refreshOrderInBackground();
    } catch (e) {
      setError(formatFirebaseError(e));
    } finally {
      setSubmittingPickup(false);
    }
  }

  async function handleDropoffConfirm() {
    if (!order || !dropoffEmployeeId || !dropoffStartAt) return;
    setSubmittingDropoff(true);
    setError(null);
    try {
      await scheduleWebOrderDropoff({
        claimCode,
        email,
        employeeId: dropoffEmployeeId,
        startAt: dropoffStartAt,
        replace: Boolean(order.dropoffAppointment),
        locale,
      });
      setContributionMessage(t(locale, "orderDropoffSuccessBody"));
      setView("details");
      refreshOrderInBackground();
    } catch (e) {
      setError(formatFirebaseError(e));
    } finally {
      setSubmittingDropoff(false);
    }
  }

  async function handleDropoffCancel() {
    if (!order?.dropoffAppointment || cancelingDropoff) return;
    setCancelingDropoff(true);
    setError(null);
    try {
      await cancelWebOrderDropoff({
        claimCode,
        email,
        appointmentId: order.dropoffAppointment.id,
      });
      setOrder((current) => (current ? { ...current, dropoffAppointment: null } : current));
      setContributionMessage(t(locale, "orderDropoffCanceled"));
      refreshOrderInBackground();
    } catch (e) {
      setError(formatFirebaseError(e));
    } finally {
      setCancelingDropoff(false);
    }
  }

  async function handleInquiryAnswer() {
    const text = inquiryText.trim();
    if (!text || submittingInquiry) return;
    setSubmittingInquiry(true);
    setError(null);
    try {
      await answerWebOrderQuestion({ claimCode, email, text });
      setInquiryText("");
      setContributionMessage(t(locale, "orderInquirySent"));
      refreshOrderInBackground();
    } catch (e) {
      setError(formatFirebaseError(e));
    } finally {
      setSubmittingInquiry(false);
    }
  }

  const activePhaseIndex = order
    ? Math.max(0, PHASES.indexOf(order.customerPhaseKey as (typeof PHASES)[number]))
    : 0;

  const availablePickupSlots = pickupSlots.filter((slot) => slot.available);
  const hasOpenInquiry = Boolean(order?.inquiry?.openQuestionAt);

  const inquiriesSection = order ? (
    <div className="summary" style={{ marginTop: 16 }}>
      <h2 style={{ marginTop: 0 }}>{t(locale, "orderInquiries")}</h2>
      {(order.inquiries ?? []).map((message) => (
        <div key={message.id} className="option" style={{ marginTop: 8 }}>
          <div>
            <div className="option-sub">
              {message.authorRole === "customer"
                ? t(locale, "orderInquiryCustomer")
                : message.staffName || t(locale, "orderInquiryStaff")}
              {message.createdAt
                ? ` · ${formatSlotDate(new Date(message.createdAt), locale)} ${formatSlotTime(
                    new Date(message.createdAt),
                    locale
                  )}`
                : ""}
            </div>
            <div className="option-title">{message.text}</div>
          </div>
        </div>
      ))}
      <label className="form-grid" style={{ marginTop: 12 }}>
        <textarea
          rows={3}
          value={inquiryText}
          onChange={(e) => setInquiryText(e.target.value)}
          placeholder={t(locale, "orderInquiryHint")}
        />
      </label>
      <div className="actions" style={{ marginTop: 12 }}>
        <button
          type="button"
          className={`btn btn-primary${submittingInquiry ? " loading" : ""}`}
          disabled={submittingInquiry || !inquiryText.trim()}
          onClick={() => void handleInquiryAnswer()}
        >
          {submittingInquiry ? t(locale, "loading") : t(locale, "orderInquiryAnswer")}
        </button>
      </div>
    </div>
  ) : null;

  return (
    <div className="page">
      {order && (
        <header className="header">
          {order.salon.branding.logoUrl ? (
            <img className="logo" src={order.salon.branding.logoUrl} alt="" />
          ) : (
            <div className="logo" />
          )}
          <div>
            <h1 className="salon-name">{order.salon.name}</h1>
            <p className="salon-meta">
              {[order.salon.address, order.salon.city].filter(Boolean).join(", ")}
            </p>
          </div>
        </header>
      )}

      <div className="card">
        {view === "email" && (
          <>
            <h2>{t(locale, "orderTitle")}</h2>
            <p className="muted">{t(locale, "orderEmailPrompt")}</p>
            <form className="form-grid" onSubmit={handleEmailSubmit}>
              <label>
                {t(locale, "orderEmail")}
                <input
                  type="text"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="username"
                />
                <span className="muted">{t(locale, "orderEmailHint")}</span>
              </label>
              {error && <p className="error">{error}</p>}
              <button
                type="submit"
                className={`btn btn-primary${loading ? " loading" : ""}`}
                disabled={loading || !email.trim()}
              >
                {loading ? t(locale, "loading") : t(locale, "orderView")}
              </button>
            </form>
          </>
        )}

        {view === "details" && order && (
          <>
            <h2>{order.orderNumber}</h2>
            <p className="option-title">{order.title}</p>
            {order.shortDescription && <p className="muted">{order.shortDescription}</p>}

            {order.pickupAppointment && (
              <div className="summary" style={{ marginTop: 16 }}>
                <strong>{t(locale, "orderPickupScheduled")}</strong>
                <p>{formatSlotDate(new Date(order.pickupAppointment.startAt), locale)}</p>
                <p>{formatSlotTime(new Date(order.pickupAppointment.startAt), locale)}</p>
              </div>
            )}

            {order.canSchedulePickup && (
              <div className="actions">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => {
                    setPickupDateKey(pickupDays[0] ?? null);
                    setSelectedSlot(null);
                    setError(null);
                    setView("pickup");
                  }}
                >
                  {t(locale, "orderSchedulePickup")}
                </button>
              </div>
            )}

            {order.dropoffAppointment && (
              <div className="summary" style={{ marginTop: 16 }}>
                <strong>{t(locale, "orderDropoffScheduled")}</strong>
                <p>{formatSlotDate(new Date(order.dropoffAppointment.startAt), locale)}</p>
                <p>{formatSlotTime(new Date(order.dropoffAppointment.startAt), locale)}</p>
              </div>
            )}

            {(order.canScheduleDropoff || order.dropoffAppointment) && (
              <div className="actions">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => {
                    setDropoffEmployeeId(
                      order.dropoffAppointment?.employeeId ?? order.dropoffEmployeeId
                    );
                    setDropoffStartAt(null);
                    setError(null);
                    setView("dropoff");
                  }}
                >
                  {order.dropoffAppointment
                    ? t(locale, "orderRescheduleDropoff")
                    : t(locale, "orderScheduleDropoff")}
                </button>
                {order.dropoffAppointment && (
                  <button
                    type="button"
                    className="btn btn-secondary"
                    disabled={cancelingDropoff}
                    onClick={() => void handleDropoffCancel()}
                  >
                    {t(locale, "orderCancelDropoff")}
                  </button>
                )}
              </div>
            )}

            <div className="steps" style={{ marginTop: 20 }}>
              {PHASES.map((phase, index) => (
                <span
                  key={phase}
                  className={`step-pill ${index === activePhaseIndex ? "active" : ""} ${
                    index < activePhaseIndex ? "done" : ""
                  }`}
                >
                  {t(locale, PHASE_LABELS[phase])}
                </span>
              ))}
            </div>

            <div className="summary" style={{ marginTop: 20 }}>
              <p>
                <strong>{t(locale, "orderStatus")}:</strong> {order.statusLabel}
              </p>
              {order.promisedReadyAt && (
                <p className="muted">
                  {formatSlotDate(new Date(order.promisedReadyAt), locale)}
                </p>
              )}
            </div>

            {hasOpenInquiry && inquiriesSection}

            {order.items.length > 0 && (
              <>
                <h2 style={{ marginTop: 24 }}>{t(locale, "orderItems")}</h2>
                <div className="option-grid">
                  {order.items.map((item) => {
                    const discount = Number(item.discountPercent ?? 0);
                    const effUnit =
                      discount > 0
                        ? Math.round(item.unitPriceCents * (1 - Math.min(discount, 100) / 100))
                        : item.unitPriceCents;
                    const lineTotal = Math.round(item.qty * effUnit);
                    return (
                      <div key={item.id || item.name} className="option">
                        <div>
                          <div className="option-title">{item.name}</div>
                          <div className="option-sub">
                            {formatQty(item.qty, locale)}{" "}
                            {unitLabelShort(item.unit, locale)} ×{" "}
                            {formatPrice(effUnit, order.currency, locale)}
                            {discount > 0
                              ? ` (−${discount === Math.round(discount) ? Math.round(discount) : discount.toFixed(1)} %)`
                              : ""}
                          </div>
                          {item.note ? (
                            <div className="option-sub">{item.note}</div>
                          ) : null}
                        </div>
                        <div className="option-title">
                          {formatPrice(lineTotal, order.currency, locale)}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <p style={{ marginTop: 12 }}>
                  <strong>{t(locale, "orderTotal")}:</strong>{" "}
                  {formatPrice(order.totalCents, order.currency, locale)}
                </p>
              </>
            )}

            {order.customerVisibleNote && (
              <div className="summary" style={{ marginTop: 16 }}>
                <strong>{t(locale, "orderNote")}</strong>
                <p>{order.customerVisibleNote}</p>
              </div>
            )}

            <div className="summary" style={{ marginTop: 24 }}>
              <h2 style={{ marginTop: 0 }}>{t(locale, "orderCustomerPhotosNotes")}</h2>
              <label className="form-grid" style={{ marginTop: 12 }}>
                <textarea
                  rows={3}
                  value={customerNoteText}
                  onChange={(e) => setCustomerNoteText(e.target.value)}
                  placeholder={t(locale, "orderCustomerNoteHint")}
                />
              </label>
              <div className="actions" style={{ marginTop: 12 }}>
                <label className="btn btn-secondary" style={{ cursor: uploadingPhoto ? "wait" : "pointer" }}>
                  {uploadingPhoto ? t(locale, "orderUploading") : t(locale, "orderCustomerAddPhoto")}
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    style={{ display: "none" }}
                    disabled={uploadingPhoto}
                    onChange={(e) => {
                      const file = e.target.files?.[0] ?? null;
                      void handlePhotoSelected(file);
                      e.target.value = "";
                    }}
                  />
                </label>
                <button
                  type="button"
                  className={`btn btn-primary${submittingNote ? " loading" : ""}`}
                  disabled={submittingNote || !customerNoteText.trim()}
                  onClick={() => void handleAddNote()}
                >
                  {submittingNote ? t(locale, "loading") : t(locale, "orderCustomerAddNote")}
                </button>
              </div>
              {contributionMessage && <p className="muted">{contributionMessage}</p>}
            </div>

            {(order.customerNotes ?? []).length > 0 && (
              <div className="summary" style={{ marginTop: 16 }}>
                <strong>{t(locale, "orderCustomerNotes")}</strong>
                {(order.customerNotes ?? []).map((note) => (
                  <div key={note.id} className="option" style={{ marginTop: 8 }}>
                    {note.createdAt && (
                      <div className="option-sub">
                        {formatSlotDate(new Date(note.createdAt), locale)}{" "}
                        {formatSlotTime(new Date(note.createdAt), locale)}
                      </div>
                    )}
                    <div className="option-title">{note.text}</div>
                  </div>
                ))}
              </div>
            )}

            {(order.customerAttachments ?? []).length > 0 && (
              <div className="summary" style={{ marginTop: 16 }}>
                <strong>{t(locale, "orderCustomerPhotos")}</strong>
                <div className="day-row" style={{ marginTop: 8 }}>
                  {(order.customerAttachments ?? []).map((photo) =>
                    photo.url ? (
                      <a key={photo.id} href={photo.url} target="_blank" rel="noreferrer">
                        <img
                          src={photo.url}
                          alt={photo.fileName}
                          style={{
                            width: 120,
                            height: 120,
                            objectFit: "cover",
                            borderRadius: 8,
                          }}
                        />
                      </a>
                    ) : null
                  )}
                </div>
              </div>
            )}

            {!hasOpenInquiry && inquiriesSection}

            {error && <p className="error">{error}</p>}
          </>
        )}

        {view === "dropoff" && order && (
          <>
            <h2>{t(locale, "orderScheduleDropoff")}</h2>
            <p className="muted">{order.orderNumber}</p>
            <SlotPicker
              locale={locale}
              employeeId={dropoffEmployeeId}
              loadSlots={(dateKey) =>
                fetchWebOrderDropoffAvailability({
                  claimCode,
                  email,
                  date: dateKey,
                  employeeId: dropoffEmployeeId ?? undefined,
                })
              }
              selectedStartAt={dropoffStartAt}
              onEmployeeId={setDropoffEmployeeId}
              onSelect={(slot) => setDropoffStartAt(slot.startAt)}
            />
            {dropoffStartAt && (
              <div className="summary" style={{ marginTop: 20 }}>
                <p>{formatSlotDate(new Date(dropoffStartAt), locale)}</p>
                <p>{formatSlotTime(new Date(dropoffStartAt), locale)}</p>
              </div>
            )}
            {error && <p className="error">{error}</p>}
            <div className="actions">
              <button
                type="button"
                className="btn btn-secondary"
                disabled={submittingDropoff}
                onClick={() => setView("details")}
              >
                {t(locale, "back")}
              </button>
              <button
                type="button"
                className={`btn btn-primary${submittingDropoff ? " loading" : ""}`}
                disabled={!dropoffStartAt || !dropoffEmployeeId || submittingDropoff}
                onClick={() => void handleDropoffConfirm()}
              >
                {submittingDropoff ? t(locale, "booking") : t(locale, "orderConfirmDropoff")}
              </button>
            </div>
          </>
        )}

        {view === "pickup" && order && (
          <>
            <h2>{t(locale, "orderSchedulePickup")}</h2>
            <p className="muted">{order.orderNumber}</p>

            <h2 style={{ marginTop: 20 }}>{t(locale, "chooseDay")}</h2>
            <div className="day-row">
              {pickupDays.map((day) => (
                <button
                  key={day}
                  type="button"
                  className={`chip ${pickupDateKey === day ? "selected" : ""}`}
                  onClick={() => {
                    setPickupDateKey(day);
                    setSelectedSlot(null);
                  }}
                >
                  {new Intl.DateTimeFormat(locale, {
                    weekday: "short",
                    day: "numeric",
                    month: "short",
                    timeZone: "Europe/Berlin",
                  }).format(berlinLocalDate(day))}
                </button>
              ))}
            </div>

            {pickupDateKey && (
              <>
                <h2 style={{ marginTop: 20 }}>{t(locale, "chooseSlot")}</h2>
                {pickupLoading ? (
                  <p className="muted">{t(locale, "loading")}</p>
                ) : availablePickupSlots.length === 0 ? (
                  <p className="muted">{t(locale, "noSlots")}</p>
                ) : (
                  <div className="slot-row">
                    {availablePickupSlots.map((slot) => {
                      const start = new Date(slot.startAt);
                      const selected = selectedSlot?.toISOString() === slot.startAt;
                      return (
                        <button
                          key={slot.startAt}
                          type="button"
                          className={`chip ${selected ? "selected" : ""}`}
                          onClick={() => setSelectedSlot(start)}
                        >
                          {formatSlotTime(start, locale)}
                        </button>
                      );
                    })}
                  </div>
                )}
              </>
            )}

            {error && <p className="error">{error}</p>}

            <div className="actions">
              <button type="button" className="btn btn-secondary" onClick={() => setView("details")}>
                {t(locale, "back")}
              </button>
              <button
                type="button"
                className={`btn btn-primary${submittingPickup ? " loading" : ""}`}
                disabled={!selectedSlot || submittingPickup}
                onClick={handlePickupConfirm}
              >
                {submittingPickup ? t(locale, "booking") : t(locale, "orderConfirmPickup")}
              </button>
            </div>
          </>
        )}

        {view === "pickupSuccess" && order && confirmedPickup && (
          <>
            <h2>{t(locale, "orderPickupSuccessTitle")}</h2>
            <p>{t(locale, "orderPickupSuccessBody")}</p>
            <div className="summary">
              <p>{formatSlotDate(new Date(confirmedPickup.startAt), locale)}</p>
              <p>{formatSlotTime(new Date(confirmedPickup.startAt), locale)}</p>
            </div>
            <div className="actions">
              <button type="button" className="btn btn-primary" onClick={() => setView("details")}>
                {t(locale, "back")}
              </button>
            </div>
          </>
        )}
      </div>

      <p className="footer">WERKSTATE</p>
      <LegalFooter locale={locale} />
    </div>
  );
}
